"use client"

import { useState, useEffect, useRef } from "react"
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  Paper,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TablePagination
} from "@mui/material"
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Restore as RestoreIcon,
} from "@mui/icons-material"
import { API_URL } from "../../constants"
import { toast } from "react-toastify"

// Helper to parse "AM/PM" time strings into 24-hour format (e.g., "08:00" for 8 AM, "18:00" for 6 PM)
const parseTime = (timeStr) => {
  const parts = timeStr.toUpperCase().split(" ")
  const timePart = parts[0]
  const period = parts[1]

  let hours, minutes

  // Handle HH:MM or H.MM format
  if (timePart.includes(":")) {
    ;[hours, minutes] = timePart.split(":").map(Number)
  } else if (timePart.includes(".")) {
    ;[hours, minutes] = timePart.split(".").map(Number)
  } else {
    hours = Number(timePart)
    minutes = 0
  }

  // Adjust hours for AM/PM
  if (period === "PM" && hours !== 12) {
    hours += 12
  } else if (period === "AM" && hours === 12) {
    hours = 0
  }

  return { hours: isNaN(hours) ? 0 : hours, minutes: isNaN(minutes) ? 0 : minutes }
}

// Helper to parse days like "Monday - Saturday" or "Sunday, Monday"
const parseDays = (daysStr) => {
  const daysMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  }
  const allowedDays = new Set()

  if (daysStr.includes("-")) {
    const [startDay, endDay] = daysStr.split("-").map((s) => s.trim().toLowerCase())
    const startIndex = daysMap[startDay]
    const endIndex = daysMap[endDay]

    // Handle wrap around (e.g., Friday - Monday)
    if (startIndex > endIndex) {
      for (let i = startIndex; i <= 6; i++) allowedDays.add(i)
      for (let i = 0; i <= endIndex; i++) allowedDays.add(i)
    } else {
      // Normal range (e.g., Monday - Saturday)
      for (let i = startIndex; i <= endIndex; i++) allowedDays.add(i)
    }
  } else {
    // Comma-separated days
    daysStr.split(",").map((s) => {
      const dayIndex = daysMap[s.trim().toLowerCase()]
      if (dayIndex !== undefined) allowedDays.add(dayIndex)
    })
  }
  return allowedDays
}

// Helper to format 24-hour to 12-hour for display
const formatHour12 = (h) => {
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h
  return hour12.toString()
}

// Helper to format date and time consistently
const formatDateTime = (dateString) => {
  if (!dateString) return "-"
  const date = new Date(dateString)
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
}

// Helper function to parse CSV string into an array of objects
const parseCsv = (csvString) => {
  const lines = csvString.trim().split("\n")
  if (lines.length === 0) return []

  const headers = lines[0].split(",").map((h) => h.replace(/"/g, "").trim()) // Remove quotes from headers
  const data = []

  for (let i = 1; i < lines.length; i++) {
    // Regex to split by comma, but not inside double quotes
    const values = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => v.replace(/"/g, "").trim())
    const row = {}
    headers.forEach((header, index) => {
      row[header.toLowerCase()] = values[index] // Map to lowercase keys for consistency
    })
    data.push(row)
  }
  return data
}

// Helper function to remove a specific country code from a phone number string
const removeSpecificCountryCode = (phoneNumber, countryCode) => {
  if (typeof phoneNumber !== "string" || !countryCode) return phoneNumber
  // Create a regex that matches the specific country code at the beginning of the string
  // Escape special characters in countryCode (like '+')

  // const escapedCountryCode = countryCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  // const regex = new RegExp(`^${escapedCountryCode}\\s?`) // Match country code followed by optional space
  // return phoneNumber.replace(regex, "")
  return phoneNumber;
}

function CampaignReportDialog({ open, onClose, campaign, userInfo }) {
  const [activeTab, setActiveTab] = useState("admin") // 'admin' or 'user'
  const [adminReportData, setAdminReportData] = useState([])
  const [userReportData, setUserReportData] = useState([])
  const [loadingReport, setLoadingReport] = useState(false)
  const [uploadingReport, setUploadingReport] = useState(false)
  const [selectedUploadFile, setSelectedUploadFile] = useState(null)
  const [isEditedUserReport, setIsEditedUserReport] = useState(false)
  const fileInputRef = useRef(null)
  // New states for Change Report Dialog
  const [openChangeReportDialog, setOpenChangeReportDialog] = useState(false)
  const [newNonWhatsappPercentage, setNewNonWhatsappPercentage] = useState("")
  const [reportType, setReportType] = useState("inc") // 'increase' or 'decrease'
  const [generatingReport, setGeneratingReport] = useState(false)
  const [userNonWhatsappCount, setUserNonWhatsappCount] = useState(0)

  // Pagination states
  const [adminPage, setAdminPage] = useState(0)
  const [userPage, setUserPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [campaignFailed, setCampaignFailed] = useState("");

  useEffect(() => {
  const count = userReportData.filter(row => row?.status === "Non-WhatsApp").length
  setUserNonWhatsappCount(count)
}, [userReportData])

  useEffect(() => {
    if (open && campaign) {
      // Reset states when dialog opens
      // If user is not admin, default to user tab
      if (userInfo?.user?.role !== "admin") {
        setActiveTab("user")
      } else {
        setActiveTab("admin")
      }
      setAdminReportData([])
      setUserReportData([])
      setSelectedUploadFile(null)
      fetchReportData()
      setIsEditedUserReport(false)
      if (campaign.isUserReportChanged === true) {
        setIsEditedUserReport(true)
      }

      // reset pagination when dialog opens
      setAdminPage(0)
      setUserPage(0)
    }
  }, [open, campaign, userInfo?.user?.role]) // Add userInfo.user.role to dependencies
  const fetchReportData = async () => {
    if (!campaign || !userInfo?.token) return

    setLoadingReport(true)
    try {
      // Fetch Admin Report Data (only if user is admin)
      if (userInfo?.user?.role === "admin") {
        try {
           setCampaignFailed(""); // Reset campaignFailed before new fetch
          const adminResponse = await fetch(`${API_URL}/api/campaigns/${campaign._id}/report/admin`, {
            headers: { Authorization: `Bearer ${userInfo.token}` },
          })
          const contentType = adminResponse.headers.get("content-type");
         
          if (!adminResponse.ok) {
            // Handle JSON error response
            if (contentType && contentType.includes("application/json")) {
              const errorData = await adminResponse.json();
              if (errorData.code === 405) {
                setCampaignFailed(errorData.message);
                throw new Error(errorData.message);
              }
            }
            throw new Error("Failed to fetch user report");
          }

          // If CSV response
          if (contentType && contentType.includes("text/csv")) {
            const userCsvText = await adminResponse.text();
            const parsedUserData = parseCsv(userCsvText);
            setAdminReportData(parsedUserData);
          }
          // If JSON success response (optional case)
          else if (contentType && contentType.includes("application/json")) {
            const jsonData = await adminResponse.json();
            if (jsonData.csvText) {
              const parsedUserData = parseCsv(jsonData.csvText);
              setAdminReportData(parsedUserData);
            } else {
              setAdminReportData([]);
            }
          } else {
            setAdminReportData([]);
          }



        } catch (error) {
          toast.error(`Failed to load admin report: ${error.message}`)
          setAdminReportData([]) // Clear data on error
        }
      }

      // Fetch User Report Data (for all roles)
      try {
         setCampaignFailed(""); // Reset campaignFailed before new fetch
        const userResponse = await fetch(`${API_URL}/api/campaigns/${campaign._id}/report/user`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        // Detect response type
        const contentType = userResponse.headers.get("content-type");

        if (!userResponse.ok) {
          // Handle JSON error response
          if (contentType && contentType.includes("application/json")) {
            const errorData = await userResponse.json();
            if (errorData.code === 405) {
              setCampaignFailed(errorData.message);
              throw new Error(errorData.message);
            }
          }
          throw new Error("Failed to fetch user report");
        }

        // If CSV response
        if (contentType && contentType.includes("text/csv")) {
          const userCsvText = await userResponse.text();
          const parsedUserData = parseCsv(userCsvText);
          setUserReportData(parsedUserData);
        }
        // If JSON success response (optional case)
        else if (contentType && contentType.includes("application/json")) {
          const jsonData = await userResponse.json();
          if (jsonData.csvText) {
            const parsedUserData = parseCsv(jsonData.csvText);
            setUserReportData(parsedUserData);
          } else {
            setUserReportData([]);
          }
        } else {
          setUserReportData([]);
        }

      } catch (error) {
        toast.error(`Failed to load user report: ${error.message}`);
        setUserReportData([]); // Clear data on error
      }

    } catch (error) {
      toast.error(`An unexpected error occurred while loading reports: ${error.message}`)
    } finally {
      setLoadingReport(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleDownloadAdminReport = () => {
    if (adminReportData.length === 0) {
      toast.info("No data to download.")
      return
    }

    // REMOVED "From" from headers
    const headers = ["Number","FromNumber", "Status", "Time", "White", "Common", "Country", "WhatsApp"]
    const csvRows = []

    // Add headers
    csvRows.push(headers.map((h) => `"${h}"`).join(",")) // Ensure headers are quoted

    // Add data rows
    adminReportData.forEach((row) => {
      const values = [
        removeSpecificCountryCode(row.number, campaign.countryCode), // Apply transformation here for CSV
        row.fromnumber,
        row.status,
        row.time,
        row.white,
        row.common,
        row.country,
        row.whatsapp,
      ]
      csvRows.push(values.map((v) => `"${v}"`).join(",")) // Wrap values in quotes to handle commas
    })

    const csvString = csvRows.join("\n")
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", `admin_report_${campaign.title.replace(/\s+/g, "_")}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    //toast.success("Admin report downloaded successfully!")
  }

  const handleDownloadUserReport = () => {
    if (userReportData.length === 0) {
      toast.info("No data to download.")
      return
    }

    // Updated headers to match backend's getUserReport
    //const headers = ["Number", "Status", "Country", "WhatsApp"]
    const headers = ["Number", "Status"]
    const csvRows = []

    // Add headers
    csvRows.push(headers.map((h) => `"${h}"`).join(",")) // Ensure headers are quoted

    // Add data rows
    userReportData.forEach((row) => {
      const values = [
        removeSpecificCountryCode(row.number, campaign.countryCode), // Apply transformation here for CSV
        row.status,
        // row.country,
        // row.whatsapp,
      ]
      csvRows.push(values.map((v) => `"${v}"`).join(",")) // Wrap values in quotes to handle commas
    })

    const csvString = csvRows.join("\n")
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", `user_report_${campaign.title.replace(/\s+/g, "_")}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    //toast.success("User report downloaded successfully!")
  }

  const handleChooseReportFile = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedUploadFile(file)
      toast.info(`Selected file: ${file.name}`)
    }
  }

  const handleUploadReport = async () => {
    if (!selectedUploadFile) {
      toast.error("Please choose a file to upload.")
      return
    }
    if (!campaign || !userInfo?.token) {
      toast.error("Campaign or user info missing.")
      return
    }

    setUploadingReport(true)
    try {
      const formData = new FormData()
      formData.append("reportFile", selectedUploadFile)

      const response = await fetch(`${API_URL}/api/campaigns/${campaign._id}/report/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${userInfo.token}` }, // No 'Content-Type' header for FormData
        body: formData,
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to upload report")
      }
      const result = await response.json()

     // toast.success("Report uploaded successfully!")
      setSelectedUploadFile(null) // Clear selected file after upload
      if (fileInputRef.current) {
        fileInputRef.current.value = "" // Clear file input
      }
      // Re-fetch report data after upload to display the new report
      fetchReportData()
    } catch (error) {
      toast.error(`Failed to upload report: ${error.message}`)
    } finally {
      setUploadingReport(false)
    }
  }

  // Handlers for Change Report Dialog
  const handleOpenChangeReportDialog = () => {
    setOpenChangeReportDialog(true)

    setNewNonWhatsappPercentage("")
    setReportType("inc")
  }

  const handleCloseChangeReportDialog = () => {
    setOpenChangeReportDialog(false)
    setNewNonWhatsappPercentage("") // Reset input on close
    setReportType("inc") // Reset type on close
  }
  const handleResetUserReport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaign._id}/report/reset-user-report`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to Reset report")
      }
      const result = await response.json()
      setIsEditedUserReport(false)
      //toast.success("Report reset initiated successfully!")
      handleCloseChangeReportDialog()
      fetchReportData()
    } catch (error) {
      toast.error(`Failed to reset report: ${error.message}`)
    } finally {
      onClose()
    }
  }
  const handleGenerateReport = async () => {
    if (!newNonWhatsappPercentage || isNaN(Number.parseFloat(newNonWhatsappPercentage))) {
      toast.error("Please enter a valid percentage.")
      return
    }
    if (!campaign || !userInfo?.token) {
      toast.error("Campaign or user info missing.")
      return
    }

    setGeneratingReport(true)
    try {
      // Placeholder for API call - replace with actual endpoint and logic later
      const response = await fetch(`${API_URL}/api/campaigns/${campaign._id}/report/edit-user-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({
          type: reportType,
          percentage: Number.parseFloat(newNonWhatsappPercentage),
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to generate report")
      }

      const result = await response.json()

     // setIsEditedUserReport(true)

      //toast.success("Report generation initiated successfully!")
      handleCloseChangeReportDialog() // Close dialog on success
      // Optionally re-fetch user report data if the backend updates it immediately
      fetchReportData()
      onClose()
    } catch (error) {
      toast.error(`Failed to generate report: ${error.message}`)
    } finally {
      setGeneratingReport(false)
    }
  }

  if (!campaign) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Campaign Report: {campaign.title}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>

        {
          campaignFailed !== "" ? <Alert severity="error" sx={{ mx: 3, padding: 3 }}>{campaignFailed}</Alert> : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="report tabs">
                  {userInfo?.user?.role === "admin" && <Tab label="Admin Report" value="admin" />}
                  <Tab label="User Report" value="user" />
                </Tabs>
              </Box>

              {activeTab === "admin" && userInfo?.user?.role === "admin" && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Admin Report Details
                  </Typography>

                  {/* Download Admin Report and Update User Report Buttons */}
                  <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadAdminReport}
                      disabled={loadingReport || adminReportData.length === 0}
                      sx={{ bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
                    >
                      Download Admin Report
                    </Button>
                    {!isEditedUserReport && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={handleOpenChangeReportDialog}
                      // disabled ={campaign.isUserReportChanged}
                      >
                        Update User Report
                      </Button>
                    )}

                    {isEditedUserReport && (
                      <Button variant="contained" color="primary" startIcon={<RestoreIcon />} onClick={handleResetUserReport}>
                        Reset User Report
                      </Button>
                    )}
                  </Box>

                  {/* Upload Edited Report */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Upload Edited Report (for Users/Resellers)
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleChooseReportFile}
                        style={{ display: "none" }}
                        accept=".csv, .xlsx" // Specify accepted file types
                      />
                      <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current.click()}
                        disabled={uploadingReport}
                      >
                        Choose Report File
                      </Button>
                      {selectedUploadFile && (
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {selectedUploadFile.name}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={uploadingReport ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        onClick={handleUploadReport}
                        disabled={!selectedUploadFile || uploadingReport}
                      >
                        {uploadingReport ? "Uploading..." : "Upload Report"}
                      </Button>
                    </Box>
                  </Box>

                  {/* Admin Report Table */}
                  {loadingReport ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : adminReportData.length > 0 ? (
                    <Paper variant="outlined">
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {/* REMOVED "From" column */}
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Number</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>FromNumber</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Time</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>White</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Common</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Country</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>WhatsApp</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {adminReportData
                              .slice(adminPage * rowsPerPage, adminPage * rowsPerPage + rowsPerPage)
                              .map((row, index) => (
                                <TableRow key={index}>
                                  {/* REMOVED "From" cell */}
                                  <TableCell sx={{ textAlign: "left" }}>
                                    {removeSpecificCountryCode(row.number, campaign.countryCode)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>
                                   {row.fromnumber}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{row.status}</TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{row.time ? row.time : "-"}</TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>{row.white}</TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>{row.common}</TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{row.country}</TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>{row.whatsapp}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        rowsPerPageOptions={[50, 100, 200]}
                        count={adminReportData.length}
                        rowsPerPage={rowsPerPage}
                        page={adminPage}
                        onPageChange={(e, newPage) => setAdminPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10))
                          setAdminPage(0)
                        }}
                      />
                    </Paper>


                  ) : (
                    <Alert severity="info">No admin report data available for this campaign.</Alert>
                  )}
                </Box>
              )}

              {activeTab === "user" && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    User Report Details - NonWhatsapp Count ({userNonWhatsappCount})
                  </Typography>
                  {userInfo?.user?.role === "admin" && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This section shows the report as a regular user or reseller would see it.
                    </Alert>
                  )}

                  {/* Download User Report */}
                  <Box sx={{ mb: 3 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadUserReport}
                      disabled={loadingReport || userReportData.length === 0}
                    >
                      Download User Report
                    </Button>
                  </Box>

                  {/* User Report Table (simplified) */}
                  {loadingReport ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : userReportData.length > 0 ? (
                    <Paper variant="outlined">
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {/* Updated headers for user report */}
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Number</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Status</TableCell>
                              {/* <TableCell sx={{ fontWeight: "bold", textAlign: "left" }}>Country</TableCell>
                              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>WhatsApp</TableCell> */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {userReportData
                              .slice(userPage * rowsPerPage, userPage * rowsPerPage + rowsPerPage)
                              .map((row, index) => (
                                <TableRow key={index}>
                                  {/* Updated cells for user report */}
                                  <TableCell sx={{ textAlign: "left" }}>
                                    {removeSpecificCountryCode(row.number, campaign.countryCode)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{row.status}</TableCell>
                                  {/* <TableCell sx={{ textAlign: "left" }}>{row.country}</TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>{row.whatsapp}</TableCell> */}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        rowsPerPageOptions={[50, 100, 200]}
                        count={userReportData.length}
                        rowsPerPage={rowsPerPage}
                        page={userPage}
                        onPageChange={(e, newPage) => setUserPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10))
                          setUserPage(0)
                        }}
                      />
                    </Paper>

                  ) : (
                    <Alert severity="info">No user report data available for this campaign.</Alert>
                  )}
                </Box>
              )}
            </>
          )
        }

      </DialogContent>

      {/* Change Report Dialog */}
      <Dialog open={openChangeReportDialog} onClose={handleCloseChangeReportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Report
          <IconButton
            aria-label="close"
            onClick={handleCloseChangeReportDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">Total Numbers: {adminReportData.length || 0}</Typography>
            <Typography variant="body1">
              Initial Non Whatsapp Numbers:{" "}
              {(() => {
                const initialNonWhatsappNumbers = adminReportData.filter((row) => row.whatsapp !== "TRUE")
                return initialNonWhatsappNumbers.length
              })() || 0}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Initial Non Whatsapp Percentage:{" "}
              {(() => {
                const initialNonWhatsappNumbers = adminReportData.filter((row) => row.whatsapp !== "TRUE")
                return ((initialNonWhatsappNumbers.length / adminReportData.length) * 100).toFixed(2)
              })() || 0}
              %
            </Typography>

            <Typography variant="subtitle1" gutterBottom>
              New NonWhatsapp Percentage
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={newNonWhatsappPercentage}
              onChange={(e) => setNewNonWhatsappPercentage(e.target.value)}
              placeholder="0"
              inputProps={{ min: "0", max: "100", step: "1" }}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" gutterBottom>
              Type
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <ToggleButtonGroup
                value={reportType}
                exclusive
                onChange={(event, newType) => {
                  if (newType !== null) {
                    setReportType(newType)
                  }
                }}
                aria-label="report type"
              >
                <ToggleButton value="inc">Increase</ToggleButton>
                <ToggleButton value="desc">Decrease</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                color="success"
                startIcon={generatingReport ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleGenerateReport}
                disabled={
                  !newNonWhatsappPercentage || isNaN(Number.parseFloat(newNonWhatsappPercentage)) || generatingReport
                }
                sx={{ bgcolor: "#008069", "&:hover": { bgcolor: "#128C7E" } }}
              >
                {generatingReport ? "Generating..." : "Generate Report"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}

export default CampaignReportDialog
